import React from "react";
import { LucideIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";

interface FeatureCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  tooltipContent: string;
  features: string[];
  className?: string;
}

export const FeatureCard = ({ 
  icon: Icon, 
  title, 
  description, 
  tooltipContent, 
  features,
  className 
}: FeatureCardProps) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Card 
            className={`group relative p-8 surface-elevated cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary overflow-hidden ${className}`}
            tabIndex={0}
            role="article"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="flex flex-col space-y-6">
                <div className="flex items-start gap-6">
                  <div className="relative">
                    <div className="bg-gradient-to-br from-primary/20 to-accent/20 p-4 rounded-2xl shadow-neural" aria-hidden="true">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent rounded-2xl"></div>
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="text-xl font-bold mb-3 text-foreground group-hover:text-primary transition-colors">{title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{description}</p>
                  </div>
                </div>
                
                <div className="grid gap-3" role="list" aria-label={`Features of ${title}`}>
                  {features.map((feature, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-muted/60 to-muted/40 border border-border/50 transition-all hover:border-primary/30 hover:shadow-sm"
                      role="listitem"
                    >
                      <span className="w-2 h-2 bg-gradient-to-r from-primary to-accent rounded-full flex-shrink-0" aria-hidden="true" />
                      <span className="text-foreground font-medium text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <p>{tooltipContent}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};